import debounce from 'lodash/debounce.js'
import { gsap } from 'gsap'
export default class ExternalSources {
	constructor(videoEl) {
		this.videoEl = videoEl
		this.sources = this.parseSources()
		// Check if data-hdpr is enabled
		this.highDpr = videoEl.getAttribute('data-hdpr') === 'enabled'
		this.currentSource = null
		// Create a debounced callback for resize updates
		this.resizeHandler = debounce(this.updateSource.bind(this), 150)
		this.resizeObserver = null
		this.fallbackResizeHandler = this.resizeHandler
		// Determine the resize type: default is "element", but can be "window"
		this.resizeType = videoEl.getAttribute('data-resize-type') || 'element'

		this.init()
	}

	// Extract and prepare sources from a nested script tag
	parseSources() {
		const scriptEl = this.videoEl.querySelector('script[type="application/json"]')
		if (!scriptEl) {
			console.warn('No JSON source data found in the video element.')
			return []
		}
		const rawSources = JSON.parse(scriptEl.textContent)
		let prevWidth = 0
		// Prepare and reverse the array so that larger resolutions are prioritized
		const prepared = rawSources
			.map((source) => {
				const querySize = prevWidth
				prevWidth = source.width
				return {
					querySize: Number(querySize) + 1,
					width: source.width,
					height: source.height,
					type: source.type,
					url: source.url,
				}
			})
			.reverse()
		return prepared
	}

	// Determine the best source based on the current size and device resolution
	updateSource() {
		// Use window innerWidth if data-resize-type is "window", otherwise use element width
		const baseWidth = this.resizeType === 'window' ? window.innerWidth : this.videoEl.clientWidth
		let neededResolution = baseWidth
		if (this.highDpr) {
			neededResolution *= window.devicePixelRatio
		}

		// Find the first source whose querySize is lower than neededResolution,
		// or fall back to the smallest available source
		const bestSource = this.sources.find((source) => neededResolution > source.querySize) || this.sources[this.sources.length - 1]

		if (!bestSource) {
			console.warn('No matching video source found.')
			return
		}

		// Only update if:
		// 1. No current source is set, or
		// 2. The new best source is larger than the current source,
		//     ensuring that we only upgrade and never downgrade the quality.
		if (this.currentSource && bestSource.querySize <= this.currentSource.querySize) {
			return
		}

		// Update the current source and the video element
		this.currentSource = bestSource
		this.videoEl.setAttribute('src', bestSource.url)
		this.videoEl.load()
	}

	// Hide the poster image using GSAP when the video starts playing
	hidePoster() {
		const poster = this.videoEl.parentNode.querySelector('.js-external-video-poster')
		if (poster) {
			gsap.to(poster, {
				opacity: 0,
				duration: 0.1,
				onComplete: () => {
					poster.style.display = 'none'
				},
			})
		}
	}

	// Call this to initialize the component
	init() {
		// Pause and update the source before playback
		this.videoEl.pause()
		this.updateSource()

		// Hide the poster when the video starts playing
		this.videoEl.addEventListener(
			'playing',
			() => {
				this.hidePoster()
			},
			{ once: true },
		)

		// Set up resize tracking based on the resize type
		if (this.resizeType === 'window') {
			// Use a window resize listener
			window.addEventListener('resize', this.resizeHandler)
		} else if ('ResizeObserver' in window) {
			// Use ResizeObserver for the video element
			this.resizeObserver = new ResizeObserver(() => {
				this.resizeHandler()
			})
			this.resizeObserver.observe(this.videoEl)
		} else {
			// Fallback for browsers that don't support ResizeObserver
			window.addEventListener('resize', this.fallbackResizeHandler)
		}
	}

	destroy() {
		if (this.resizeType === 'window') {
			window.removeEventListener('resize', this.resizeHandler)
		} else if (this.resizeObserver) {
			this.resizeObserver.disconnect()
			this.resizeObserver = null
		} else {
			window.removeEventListener('resize', this.fallbackResizeHandler)
		}
	}
}
